import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Pagination, Input, Modal } from "antd";
import moment from "moment";

import { MyNotesAction } from "../../store/actions";

import useScreenSize from "../../utils/usescreensize";

import DashboardCard from "../../components/dashboardcard/dashboardcard";
import { PrimaryButton, WhiteButton } from "../../components/button";
import Cardloader from "../../components/loader/cardloader";
import Spacer from "../../components/spacer/spacer";
import UnderHeaderMenu from "../../components/underheadermenu";
import SortDropdown from "../../components/dropdown/sortdropdown";

import images from "../../assets";
import "./styles.css";

const { TextArea } = Input;

const MyNotes = () => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();
  const [searchParams, setSearchParams] = useSearchParams();

  const defaultPage = parseInt(searchParams.get("page")) || 1;

  const { myNotes, isLoading } = useSelector((state) => state?.myNotes);

  const [state, setState] = useState({
    sortOption: "desc",
  });
  const [pageNo, setPageNo] = useState(defaultPage);
  const [pageSize] = useState(8);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [note, setNote] = useState("");
  const [noteObject, setNoteObject] = useState(null);
  const [name, setName] = useState("");

  useEffect(() => {
    fetchNotes();
  }, [state.sortOption]);

  useEffect(() => {
    setSearchParams({ page: pageNo.toString() });
  }, [pageNo]);

  useEffect(() => {
    const urlPage = parseInt(searchParams.get("page")) || 1;
    if (urlPage !== pageNo) {
      setPageNo(urlPage);
    }
  }, [searchParams]);

  const fetchNotes = () => {
    const payload = {
      sort: state?.sortOption,
    };
    dispatch(MyNotesAction.getNotes(payload));
  };

  const sortedNotes = useMemo(() => {
    if (!myNotes) return [];
    let sortedData = [...myNotes];

    if (state.sortOption === "desc") {
      sortedData.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
    } else {
      sortedData.sort((a, b) => new Date(a.updatedAt) - new Date(b.updatedAt));
    }

    return sortedData;
  }, [myNotes, state.sortOption]);

  const paginatedNotes = useMemo(() => {
    return sortedNotes.slice((pageNo - 1) * pageSize, pageNo * pageSize);
  }, [sortedNotes, pageNo, pageSize]);

  const getNote = (username) => {
    const payload = { username };
    dispatch(
      MyNotesAction.getNote(payload, (res) => {
        setIsModalOpen(true);
        setNoteObject(res);
        setNote(res?.note);
      })
    );
  };

  const handleSendNote = () => {
    setIsModalOpen(false);
    const payload = { note, receiverId: noteObject?.receiverId };
    dispatch(MyNotesAction.setNotes(payload));
  };

  return (
    <div>
      {screenSize.width < 1023 && (
        <UnderHeaderMenu titlename="My Notes">
          <div style={{ width: "110px" }}>
            <SortDropdown
              data={[
                { label: "Newest First", value: "desc" },
                { label: "Oldest First", value: "asc" },
              ]}
              onChange={(e) => setState({ ...state, sortOption: e })}
              value={state?.sortOption || undefined}
              placeholder="Sort by"
            />
          </div>
        </UnderHeaderMenu>
      )}
      <Spacer height="16px" />
      <div className="mynote-section-top-part-container">
        <div></div>
        {screenSize.width > 1023 && (
          <div className="mynote-section-header-container">
            <h1 className="mynote-header">My Notes</h1>
          </div>
        )}
        {screenSize.width > 1023 && (
          <div className="mynote-sort-container">
            <div style={{ width: "150px" }}>
              <SortDropdown
                data={[
                  { label: "Newest First", value: "desc" },
                  { label: "Oldest First", value: "asc" },
                ]}
                onChange={(e) => setState({ ...state, sortOption: e })}
                value={state?.sortOption || undefined}
                placeholder="Sort by"
              />
            </div>
          </div>
        )}
      </div>
      <Spacer height="12px" />
      <div className="mynote-results-container">
        {isLoading ? (
          <Cardloader />
        ) : (
          <>
            {paginatedNotes.length > 0 ? (
              <>
                <div className="mynote-results">
                  {paginatedNotes.map((item, index) => (
                    <DashboardCard
                      item={item}
                      index={index}
                      key={index}
                      redirectedfrom="myNotes"
                    >
                      <div className="flex flex-col w-full gap-3">
                        <div className="mynote-card-text">
                          <p>Last Edited:</p>
                          <p>{moment(item?.updatedAt).fromNow()}</p>
                        </div>
                        <PrimaryButton
                          style={{ height: "32px" }}
                          text="View Note"
                          onClick={() => {
                            getNote(item?.username);
                            setName(`${item?.firstName} ${item?.lastName}`);
                          }}
                        />
                      </div>
                    </DashboardCard>
                  ))}
                </div>
                <Spacer height="36px" />
                <div className="flex justify-center items-center">
                  <Pagination
                    pageSize={pageSize}
                    total={sortedNotes.length}
                    current={pageNo}
                    showSizeChanger={false}
                    onChange={(page) => setPageNo(page)}
                  />
                </div>
                <Spacer height="24px" />
              </>
            ) : (
              <div className="flex flex-col justify-center items-center p-10 w-full gap-4">
                <img
                  src={images.noActivityIcon}
                  alt="no notifications"
                  className="h-[160px] lg:h-full"
                />
                <div className="text-basePrimary font-bold text-[24px] lg:text-[32px]">
                  No Notes Yet
                </div>
                <p className="text-[14px] text-center max-w-[500px]">
                  Add notes on profiles to remember important details.
                </p>
              </div>
            )}
          </>
        )}

        <Modal
          centered={true}
          open={isModalOpen}
          onCancel={() => {
            setNote("");
            setNoteObject(null);
            setName(null);
            setIsModalOpen(false);
          }}
          footer={false}
          closable={false}
          className="forgot-password-modal"
        >
          <h1
            style={{ position: "absolute", top: "10px" }}
            className="mynote-header"
          >
            My Notes
          </h1>
          <img
            src={images?.closeIcon}
            className="modal-close-icon"
            alt=""
            onClick={() => {
              setIsModalOpen(false);
              setNote("");
              setNoteObject(null);
              setName(null);
            }}
          />
          <div className="user-report-user-content">
            <div className="user-report-body">
              <div className="user-report-heading">
                <p
                  style={{
                    color: "#222",
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    fontWeight: "400",
                  }}
                >
                  Add your personal note about {name}
                </p>
              </div>
              <div className="user-report-message">
                <TextArea
                  showCount
                  maxLength={1000}
                  rows="10"
                  placeholder="Type your personal note ..."
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
            </div>
            <Spacer height="48px" />
            <div className="user-report-footer">
              <div className="user-report-btn">
                <WhiteButton
                  style={{
                    border: "2px solid var #EAE6ED",
                  }}
                  text={"Close"}
                  onClick={() => {
                    setNote("");
                    setNoteObject(null);
                    setName(null);
                    setIsModalOpen(false);
                  }}
                />
                <PrimaryButton text={"Save"} onClick={handleSendNote} />
              </div>
            </div>
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default MyNotes;
